import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDialogFocus, useScrollLock } from '../lib/hooks';
import { IconClose } from './Icons';
import { IconButton } from './IconButton';
import styles from './ui.module.css';

export interface SheetProps {
  open: boolean;
  onClose(): void;
  title: string;
  /** Hide the visual title but keep it for screen readers. */
  hideTitle?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

/**
 * Bottom sheet on phones, centred modal on desktop. Traps focus, restores it
 * on close, and closes on Escape — this is where destructive actions live.
 */
export function Sheet({ open, onClose, title, hideTitle, children, footer, wide }: SheetProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useScrollLock(open);
  useDialogFocus(open, panelRef, onClose);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.sheetRoot}>
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={[styles.panel, wide ? styles.panelWide : ''].filter(Boolean).join(' ')}
      >
        <div className={styles.grabber} aria-hidden="true" />
        <div className={styles.sheetHeader}>
          <h2 id={titleId} className={hideTitle ? 'rs-u-sr-only' : styles.sheetTitle}>
            {title}
          </h2>
          <IconButton label="Close" icon={<IconClose size={18} />} onClick={onClose} />
        </div>
        <div className={styles.sheetBody}>{children}</div>
        {footer ? <div className={styles.sheetFooter}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
